import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Target, Menu, X } from "lucide-react";
import { track } from "@/public/ui";

const NAV_LINKS = [
  { to: "/intelligence", label: "Intelligence" },
  { to: "/analyst", label: "Analyst" },
  { to: "/how-it-works", label: "How It Works" },
  { to: "/learn", label: "Learn" },
  { to: "/family", label: "Family" },
  { to: "/pricing", label: "Pricing" },
];

export function Brand({ onClick }) {
  return (
    <Link to="/" onClick={onClick} data-testid="public-brand" className="flex items-center gap-2.5 focus:outline-none focus:ring-2 focus:ring-tc-coach rounded-lg">
      <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-tc-coach to-tc-mastery flex items-center justify-center shadow-lg shadow-tc-coach/20">
        <Target className="w-5 h-5 text-white" />
      </div>
      <div className="leading-none">
        <div className="font-heading font-extrabold tracking-tight text-tc-txt">TRADECRAFT</div>
        <div className="text-[9px] font-mono uppercase tracking-[0.2em] text-tc-muted mt-0.5">Master the craft</div>
      </div>
    </Link>
  );
}

export function PublicNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const go = (to) => { setOpen(false); navigate(to); };
  const startFree = () => { track("hero_start_free_clicked", { from: "nav" }); go("/signup"); };

  return (
    <header
      data-testid="public-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${scrolled ? "tc-glass border-b border-white/10" : "bg-transparent border-b border-transparent"}`}
    >
      <div className="mx-auto max-w-6xl px-6 sm:px-8 h-16 flex items-center justify-between">
        <Brand />
        <nav className="hidden lg:flex items-center gap-7" aria-label="Primary">
          {NAV_LINKS.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => track("nav_product_clicked", { link: n.label })}
              className={`text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-tc-coach rounded px-0.5 ${location.pathname === n.to ? "text-tc-coach" : "text-tc-sub hover:text-tc-txt"}`}
            >
              {n.label}
            </Link>
          ))}
        </nav>
        <div className="hidden lg:flex items-center gap-3">
          <Link to="/login" data-testid="nav-login" className="text-sm text-tc-sub hover:text-tc-txt transition-colors focus:outline-none focus:ring-2 focus:ring-tc-coach rounded px-1">Log In</Link>
          <button data-testid="nav-start-free" onClick={startFree} className="rounded-xl bg-tc-coach text-tc-bg px-4 py-2 text-sm font-semibold hover:opacity-90 hover:shadow-[0_0_24px_rgba(6,182,212,0.35)] transition-all focus:outline-none focus:ring-2 focus:ring-tc-coach focus:ring-offset-2 focus:ring-offset-tc-bg">Start Free</button>
        </div>
        <button data-testid="mobile-nav-toggle" onClick={() => setOpen(true)} className="lg:hidden p-2 text-tc-txt" aria-label="Open menu">
          <Menu className="w-6 h-6" />
        </button>
      </div>

      {open && (
        <div className="lg:hidden fixed inset-0 z-50" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-black/70" onClick={() => setOpen(false)} />
          <div className="absolute top-0 right-0 h-full w-[82%] max-w-sm bg-[#0A0E16] border-l border-white/10 p-6 flex flex-col">
            <div className="flex items-center justify-between">
              <Brand onClick={() => setOpen(false)} />
              <button data-testid="mobile-nav-close" onClick={() => setOpen(false)} className="p-2 text-tc-sub" aria-label="Close menu"><X className="w-6 h-6" /></button>
            </div>
            <nav className="mt-8 flex flex-col gap-1" aria-label="Mobile">
              {NAV_LINKS.map((n) => (
                <button key={n.to} data-testid={`mnav-${n.label.toLowerCase().replace(/\s+/g, "-")}`} onClick={() => go(n.to)} className="text-left py-3 text-lg text-tc-txt border-b border-white/5 hover:text-tc-coach transition-colors">{n.label}</button>
              ))}
            </nav>
            <div className="mt-auto flex flex-col gap-3 pt-6">
              <button data-testid="mnav-start-free" onClick={startFree} className="rounded-xl bg-tc-coach text-tc-bg px-5 py-3 font-semibold">Start Free</button>
              <button data-testid="mnav-login" onClick={() => go("/login")} className="rounded-xl border border-white/15 text-tc-txt px-5 py-3 font-semibold">Log In</button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

export function PublicFooter() {
  const cols = [
    { h: "Product", links: [["Intelligence", "/intelligence"], ["Analyst", "/analyst"], ["How It Works", "/how-it-works"], ["Learn", "/learn"], ["Family", "/family"], ["Pricing", "/pricing"], ["Insights", "/insights"], ["Glossary", "/glossary"]] },
    { h: "Legal", links: [["Terms", "/legal"], ["Privacy", "/legal"], ["Risk Disclosure", "/legal"], ["AI Transparency", "/legal"], ["Data Attribution", "/legal"]] },
    { h: "Account", links: [["Log In", "/login"], ["Create Account", "/signup"], ["Support", "/support"]] },
  ];
  return (
    <footer data-testid="public-footer" className="relative border-t border-white/10 bg-[#0A0E16] px-6 sm:px-8 pt-14 pb-10">
      <div className="mx-auto max-w-6xl">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-10">
          <div className="col-span-2 sm:col-span-1">
            <Brand />
            <p className="mt-4 text-sm text-tc-muted max-w-xs leading-relaxed">AI-native market intelligence that develops your judgment instead of replacing it.</p>
          </div>
          {cols.map((c) => (
            <div key={c.h}>
              <div className="font-mono text-[11px] uppercase tracking-[0.16em] text-tc-sub">{c.h}</div>
              <ul className="mt-4 space-y-2.5">
                {c.links.map(([label, to], i) => (
                  <li key={`${label}-${i}`}><Link to={to} className="text-sm text-tc-muted hover:text-tc-txt transition-colors">{label}</Link></li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="mt-12 pt-6 border-t border-white/5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <p className="font-mono text-[11px] text-tc-muted">© {new Date().getFullYear()} TRADECRAFT · Learn the market. Master the craft.</p>
          <p className="font-mono text-[11px] text-tc-muted max-w-lg">Educational simulation. No financial advice, no real-money execution, no guaranteed outcomes. Trading involves risk.</p>
        </div>
      </div>
    </footer>
  );
}

export function PublicShell({ children }) {
  const location = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    track("public_page_view", { path: location.pathname });
  }, [location.pathname]);
  return (
    <div className="dark min-h-screen bg-tc-bg text-tc-txt antialiased overflow-x-hidden">
      <PublicNav />
      <main data-public-ready="true">{children}</main>
      <PublicFooter />
    </div>
  );
}
